import { useQuery } from '@tanstack/react-query'
import { BookOpen, CalendarOff, CheckCircle2, History, Plus, UserRoundCheck, UsersRound } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'
import { errorMessage } from '@/api/errors'
import { EmptyState, ErrorState, LoadingState } from '@/components/feedback/States'
import { toast } from '@/components/feedback/toast-store'
import { DataTable, type Column } from '@/components/tables/DataTable'
import { Avatar } from '@/components/ui/Avatar'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Card, CardHeader } from '@/components/ui/Card'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { PageHeader } from '@/components/ui/PageHeader'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs'
import { classesQuery } from '@/features/academic/api'
import { useAuth } from '@/features/auth/useAuth'
import { coverNeedsQuery, coversQuery, useRemoveCover } from '@/features/timetable/api'
import { CoverDialog, type CoverDraft } from '@/features/timetable/components/CoverDialog'
import {
  coverStatusLabel,
  coverStatusTone,
  dateRange,
  dutyLabel,
  dutyStatusLabel,
  dutyStatusTone,
} from '@/features/timetable/cover'
import type { Cover, CoverDuty, CoverNeed } from '@/features/timetable/types'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { subjectDotStyle } from '@/features/timetable/week'

const LOOK_AHEAD_DAYS = 60

export function CoverPage() {
  const { t } = useTranslation('timetable')
  useDocumentTitle('Substitute cover')
  const { can } = useAuth()
  const canManage = can('academic.manage')
  const [params] = useSearchParams()
  const leaveId = Number(params.get('leave')) || undefined
  const classes = useQuery(classesQuery())
  const [draft, setDraft] = useState<CoverDraft | null>(null)

  return (
    <>
      <PageHeader
        title={t('Substitute cover')}
        description={t(
          'Arrange cover for absent teachers. A substitute gets the rights they need on the covered days only.',
        )}
        breadcrumbs={[{ label: t('Timetable') }, { label: t('Substitute cover') }]}
        homeHref="/admin"
        actions={
          canManage ? (
            <Button leftIcon={<Plus />} onClick={() => setDraft({})} disabled={!classes.data}>
              {t('Arrange cover')}
            </Button>
          ) : undefined
        }
      />
      <div className="space-y-6">
        <NeedsCard
          leaveId={leaveId}
          canManage={canManage && !!classes.data}
          onAssign={(need, duty) => setDraft(draftFor(need, duty))}
        />
        <CoversCard canManage={canManage} />
      </div>
      {draft && classes.data ? (
        <CoverDialog classes={classes.data} draft={draft} onOpenChange={(open) => !open && setDraft(null)} />
      ) : null}
    </>
  )
}

function draftFor(need: CoverNeed, duty: CoverDuty): CoverDraft {
  return {
    kind: duty.kind,
    classId: duty.section.classId,
    sectionId: duty.section.id,
    subjectId: duty.subject?.id,
    startDate: need.from,
    endDate: need.to,
    leaveRequestId: need.leave.requestId,
    teacherName: need.teacher.name,
  }
}

function NeedsCard({
  leaveId,
  canManage,
  onAssign,
}: {
  leaveId: number | undefined
  canManage: boolean
  onAssign: (need: CoverNeed, duty: CoverDuty) => void
}) {
  const { t } = useTranslation('timetable')
  const needs = useQuery(coverNeedsQuery(leaveId ? { leaveRequestId: leaveId } : { days: LOOK_AHEAD_DAYS }))
  const open = needs.data?.reduce((n, need) => n + need.uncovered, 0) ?? 0

  return (
    <Card>
      <CardHeader
        icon={<CalendarOff />}
        title={leaveId ? t('Cover for this leave') : t('Absences to cover')}
        description={
          leaveId
            ? t('The duties of the teacher on leave and who covers them.')
            : t('Approved staff leave in the next {{count}} days, with the classes and lessons it affects.', {
                count: LOOK_AHEAD_DAYS,
              })
        }
        actions={
          needs.data && needs.data.length > 0 ? (
            open > 0 ? (
              <Badge tone="warning">{t('{{count}} to cover', { count: open })}</Badge>
            ) : (
              <Badge tone="success">
                <CheckCircle2 aria-hidden="true" />
                {t('All covered')}
              </Badge>
            )
          ) : undefined
        }
      />
      {needs.isPending ? (
        <LoadingState />
      ) : needs.error ? (
        <ErrorState description={errorMessage(needs.error)} onRetry={() => void needs.refetch()} />
      ) : needs.data.length === 0 ? (
        <EmptyState
          compact
          icon={<CheckCircle2 />}
          title={t('Nothing to cover')}
          description={t('No teacher with classes or lessons is on approved leave in this period.')}
        />
      ) : (
        <ul className="divide-y divide-line">
          {needs.data.map((need) => (
            <li key={need.leave.requestId} className="px-5 py-4">
              <div className="flex flex-wrap items-center gap-3">
                <Avatar name={need.teacher.name} size="sm" />
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-ink">{need.teacher.name}</p>
                  <p className="text-xs text-muted">
                    {need.leave.type} · {dateRange(need.leave.startDate, need.leave.endDate)}
                    {need.leave.halfDay ? ` · ${t('half day')}` : ''}
                  </p>
                </div>
              </div>
              <ul className="mt-3 space-y-2 sm:pl-11">
                {need.duties.map((duty) => (
                  <DutyRow
                    key={`${duty.kind}-${duty.section.id}-${duty.subject?.id ?? 0}`}
                    duty={duty}
                    onAssign={canManage && duty.status !== 'covered' ? () => onAssign(need, duty) : undefined}
                  />
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </Card>
  )
}

function DutyRow({ duty, onAssign }: { duty: CoverDuty; onAssign?: () => void }) {
  const { t } = useTranslation('timetable')
  const Icon = duty.kind === 'section' ? UsersRound : BookOpen
  return (
    <li className="flex flex-col gap-2 rounded-control border border-line bg-surface-muted/40 px-3 py-2.5 sm:flex-row sm:items-center">
      <div className="flex min-w-0 flex-1 items-start gap-2.5">
        {duty.subject ? (
          <span
            className="mt-1.5 size-2.5 shrink-0 rounded-full bg-muted"
            style={subjectDotStyle(duty.subject.color)}
          />
        ) : (
          <Icon className="mt-0.5 size-4 shrink-0 text-muted" aria-hidden="true" />
        )}
        <div className="min-w-0">
          <p className="text-sm font-medium text-ink">
            {dutyLabel(duty.kind, duty.subject)} · {duty.section.name}
          </p>
          <p className="text-xs text-muted">
            {duty.kind === 'lesson'
              ? t('{{lessons}} lessons over {{count}} days', { lessons: duty.lessons ?? 0, count: duty.days })
              : t('{{count}} school days', { count: duty.days })}
            {duty.covers.length > 0
              ? ` · ${duty.covers.map((c) => `${c.substitute} (${dateRange(c.startDate, c.endDate)})`).join(', ')}`
              : ''}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 sm:justify-end">
        <Badge tone={dutyStatusTone[duty.status]} size="sm">
          {t(dutyStatusLabel[duty.status])}
          {duty.status === 'partial' ? ` · ${duty.coveredDays}/${duty.days}` : ''}
        </Badge>
        {onAssign ? (
          <Button size="sm" variant="secondary" leftIcon={<UserRoundCheck />} onClick={onAssign}>
            {t('Assign')}
          </Button>
        ) : null}
      </div>
    </li>
  )
}

function CoversCard({ canManage }: { canManage: boolean }) {
  const { t } = useTranslation('timetable')
  const [tab, setTab] = useState<'current' | 'past'>('current')
  const current = useQuery(coversQuery('current'))
  const past = useQuery({ ...coversQuery('past'), enabled: tab === 'past' })
  const remove = useRemoveCover()
  const [removing, setRemoving] = useState<Cover | null>(null)

  const columns = useMemo<Column<Cover>[]>(
    () => [
      {
        key: 'duty',
        header: t('Cover'),
        cell: (c) => (
          <div className="min-w-0">
            <p className="font-medium text-ink">{dutyLabel(c.kind, c.subject)}</p>
            <p className="text-xs text-muted">{c.section.name}</p>
          </div>
        ),
      },
      {
        key: 'substitute',
        header: t('Substitute'),
        cell: (c) => (
          <div className="flex items-center gap-2">
            <Avatar name={c.substitute.name} size="xs" />
            <span>{c.substitute.name}</span>
          </div>
        ),
      },
      {
        key: 'for',
        header: t('Covering for'),
        hideBelow: 'lg',
        cell: (c) => c.absentTeacher?.name ?? '—',
      },
      {
        key: 'dates',
        header: t('Dates'),
        cell: (c) => <span className="tabular">{dateRange(c.startDate, c.endDate)}</span>,
      },
      {
        key: 'status',
        header: t('Status'),
        cell: (c) => (
          <Badge tone={coverStatusTone[c.status]} size="sm">
            {t(coverStatusLabel[c.status])}
          </Badge>
        ),
      },
      ...(canManage
        ? [
            {
              key: 'actions',
              header: <span className="sr-only">{t('Actions')}</span>,
              align: 'right' as const,
              cell: (c: Cover) =>
                c.status === 'ended' ? null : (
                  <Button size="sm" variant="ghost" onClick={() => setRemoving(c)}>
                    {c.status === 'active' ? t('End now') : t('Remove')}
                  </Button>
                ),
            },
          ]
        : []),
    ],
    [t, canManage],
  )

  const query = tab === 'current' ? current : past
  const onRemove = async () => {
    if (!removing) return
    try {
      await remove.mutateAsync(removing.id)
      toast.success(removing.status === 'active' ? t('Cover ended') : t('Cover removed'))
      setRemoving(null)
    } catch (e) {
      toast.error(t('Could not change the cover'), errorMessage(e))
    }
  }

  return (
    <Card>
      <Tabs value={tab} onValueChange={(v) => setTab(v === 'past' ? 'past' : 'current')}>
        <div className="border-b border-line px-5 pt-3">
          <TabsList aria-label={t('Covers')}>
            <TabsTrigger value="current" icon={<UserRoundCheck />} count={current.data?.length}>
              {t('Current and upcoming')}
            </TabsTrigger>
            <TabsTrigger value="past" icon={<History />}>
              {t('History')}
            </TabsTrigger>
          </TabsList>
        </div>
        <TabsContent value={tab} className="mt-0">
          <DataTable
            caption={tab === 'current' ? t('Current and upcoming covers') : t('Past covers')}
            columns={columns}
            rows={query.data}
            getRowId={(c) => c.id}
            loading={query.isPending}
            error={query.error}
            onRetry={() => void query.refetch()}
            empty={
              <EmptyState
                compact
                icon={<UserRoundCheck />}
                title={tab === 'current' ? t('No cover arranged') : t('No past covers')}
                description={
                  tab === 'current' ? t('Covers you arrange appear here until they end.') : undefined
                }
              />
            }
            mobileCard={(c) => (
              <div className="space-y-1.5">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-medium text-ink">
                      {dutyLabel(c.kind, c.subject)} · {c.section.name}
                    </p>
                    <p className="text-xs text-muted">
                      {c.substitute.name}
                      {c.absentTeacher ? ` · ${t('for {{name}}', { name: c.absentTeacher.name })}` : ''}
                    </p>
                  </div>
                  <Badge tone={coverStatusTone[c.status]} size="sm">
                    {t(coverStatusLabel[c.status])}
                  </Badge>
                </div>
                <div className="flex items-center justify-between gap-2 text-xs text-muted">
                  <span className="tabular">{dateRange(c.startDate, c.endDate)}</span>
                  {canManage && c.status !== 'ended' ? (
                    <Button size="sm" variant="ghost" onClick={() => setRemoving(c)}>
                      {c.status === 'active' ? t('End now') : t('Remove')}
                    </Button>
                  ) : null}
                </div>
              </div>
            )}
          />
        </TabsContent>
      </Tabs>
      {removing ? (
        <ConfirmDialog
          open
          onOpenChange={(o) => !o && setRemoving(null)}
          title={removing.status === 'active' ? t('End this cover now?') : t('Remove this cover?')}
          description={
            removing.status === 'active'
              ? t('{{name}} keeps the days already covered (until yesterday) and loses access from today.', {
                  name: removing.substitute.name,
                })
              : t('{{name}} will not cover {{what}} in {{section}}.', {
                  name: removing.substitute.name,
                  what: dutyLabel(removing.kind, removing.subject),
                  section: removing.section.name,
                })
          }
          confirmLabel={removing.status === 'active' ? t('End now') : t('Remove')}
          tone="danger"
          loading={remove.isPending}
          onConfirm={() => void onRemove()}
        />
      ) : null}
    </Card>
  )
}
